import React, { useEffect, useState } from 'react';
import { getDatabase, ref, onValue, off } from 'firebase/database';
import { MetaTags } from 'react-meta-tags';
import { useTranslation } from 'react-i18next';
import { getFirebaseBackend } from 'helpers/firebaseHelper';
import { NavLink, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { Button, Nav, NavItem, TabContent, TabPane } from 'reactstrap';
import { MoonLoader } from 'react-spinners';
import 'boxicons';
import axios from 'axios';

import { GoBackButton, GuidelineLayout } from './styles';
import Breadcrumbs from '../../components/Breadcrumb';
import Shimmer from '../../components/Shimmer';
import ApprovalChat from './components/Approval-Chat';
import PageLoading from './components/Page-Loading';
import TableResumeReview from './components/Table-Resume-Review';
import ContainerEditContent from './components/Container-Edit-Content';
import { SendCardLayout } from './components/Send-Card/styles';
import PerformanceData from './components/Performance-Data';
import TitleGuideline from './components/Title-Guideline';
import ModalCompleteReview from './components/Modal-Complete-Review';
import ButtonBack from './components/Button-Back';

const InternalApproval = () => {
  const firebaseHelper = getFirebaseBackend();
  const { id } = useParams();
  const { user } = useAuth();
  const accountId = user?.account;
  const navigate = useNavigate();
  const currentAccountId = user?.account;
  const { t: translate } = useTranslation();
  const [content, setContent] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isTasksCompleted, setIsTasksCompleted] = useState(false);
  const [activeNav, setActiveNav] = useState('1');
  const [isSending, setIsSending] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const onSubmit = async () => {
    try {
      setIsSending(true);
      await firebaseHelper.sendGuidelineToContentApproval(currentAccountId, id);

      if (content.googleDocId) {
        const body = {
          productionDate: productionDateFormatted(),
          googleDocId: content.googleDocId,
          gDriveFolders: await getGDriveFolders(),
        };

        const response = await axios.post(process.env.REACT_APP_GDOCS_WEBHOOK_DOCS_DELIVERY, body, {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_GDOCS_FLOW_KEY}`,
          },
        });

        if (response) {
          await firebaseHelper.saveGoogleDocLinkDelivery(
            currentAccountId,
            id,
            response.data.googleDocsId,
          );
        }
      }

      navigate('/old-guidelines');
    } catch (error) {
      setIsSending(false);
      console.log(error);
    }
  };

  const onBackReview = async () => {
    try {
      setIsSending(true);
      await firebaseHelper.sendGuidelineToContentReview(currentAccountId, id);
      navigate('/old-guidelines');
    } catch (error) {
      setIsSending(false);
      console.log(error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    const loadedGuideline = await firebaseHelper.getFirebaseGuideline(currentAccountId, id);

    if (loadedGuideline.column !== 'INTERNAL_APPROVAL') {
      navigate(`/old-guideline/${id}`);
    }

    setIsProcessing(loadedGuideline.processing);
    setIsError(loadedGuideline?.error);
    setContent(loadedGuideline);
    setIsLoading(false);
  };

  async function handleRetry() {
    try {
      if (!id || !currentAccountId) throw new Error('Guideline ID or Account ID is missing');
      const isProduction = process.env.REACT_APP_BUILD_TYPE === 'production';
      const firebaseToken = await firebaseHelper.getIdToken();
      setIsProcessing(true);
      setIsError(false);
      await axios.post(
        `${process.env.REACT_APP_API_URL}/internalReview`,
        {
          accountId,
          guidelineId: id,
          development: !isProduction,
        },
        {
          headers: {
            Authorization: `Bearer ${firebaseToken}`,
          },
        },
      );
    } catch (error) {
      setIsProcessing(false);
      setIsError(true);
      console.log(error);
    }
  }

  const productionDateFormatted = () => {
    const returnDate = `${content?.productionDate.year}-${content?.productionDate.month < 10 ? `0${content?.productionDate.month}` : content?.productionDate.month}`;

    return returnDate;
  };

  const getGDriveFolders = async () => {
    const data = await firebaseHelper.getGDriveFolders(accountId);

    return data;
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  useEffect(() => {
    if (!isProcessing && !isError) return;

    const db = getDatabase();
    const guidelineRef = ref(db, `guidelines/${currentAccountId}/${id}`);

    const unsubscribe = onValue(guidelineRef, (snapshot) => {
      const guidelineData = snapshot.val();

      if (!guidelineData) {
        navigate('/404');
        return;
      }

      if (guidelineData?.column !== 'INTERNAL_APPROVAL') {
        navigate(`/old-guideline/${id}`);
        return;
      }

      if (guidelineData?.processing === false || guidelineData?.error === false) {
        fetchData();
      }
    });

    return () => {
      off(guidelineRef, 'value', unsubscribe);
    };
  }, [isProcessing, isError]);

  useEffect(() => {
    fetchData();
  }, [currentAccountId]);

  useEffect(() => {
    if (!isPageLoading) return;

    const db = getDatabase();
    const guidelineRef = ref(db, `guidelines/${currentAccountId}/${id}`);

    const handleValueChange = (snapshot) => {
      const data = snapshot.val();

      if (!data?.processing) navigate(`/old-guideline/${id}`);
    };

    onValue(guidelineRef, handleValueChange);

    return () => {
      off(guidelineRef, 'value', handleValueChange);
    };
  }, [isPageLoading]);

  return (
    <GuidelineLayout>
      <div className="page-content">
        <MetaTags>
          <title>{`${translate('Internal approval')} | Ectools`}</title>
        </MetaTags>
        <Breadcrumbs title={translate('Content')} breadcrumbItem={translate('Internal approval')} />

        {isError && (
          <PageLoading
            title={translate('An error occurred')}
            description={translate('There was an error processing this guideline.')}
            button={translate('Try Again')}
            onClick={handleRetry}
            type="danger"
          >
            <box-icon name="x-circle" color="#F56565" size="7rem" />
          </PageLoading>
        )}

        {isProcessing && !isError && (
          <PageLoading
            title={translate('This guideline is being processed')}
            description={translate(
              'This may take a few minutes, you can also wait for this process in the background and return later.',
            )}
            button={translate('Go back')}
            onClick={() => navigate('/old-guidelines')}
          />
        )}

        {!isProcessing && !isError && (
          <>
            {isPageLoading ? (
              <PageLoading
                title={translate('Recreating content')}
                description={translate(
                  'This may take a few minutes, you can also wait for this process in the background and return later.',
                )}
                button={translate('Go back')}
                onClick={() => navigate('/old-guidelines')}
              />
            ) : (
              <div className="container-row">
                <div className="container-general-information">
                  <div className="header-general-information">
                    <div>
                      <ButtonBack />
                      {content?.primaryKeyword && <TitleGuideline guideline={content} />}
                    </div>
                  </div>
                  <Nav tabs>
                    <NavItem>
                      <NavLink
                        className={activeNav === '1' ? 'actived' : ''}
                        onClick={() => setActiveNav('1')}
                      >
                        {translate('Summary')}
                      </NavLink>
                    </NavItem>
                    {!content?.googleDocId && (
                      <NavItem>
                        <NavLink
                          className={activeNav === '2' ? 'actived' : ''}
                          onClick={() => setActiveNav('2')}
                        >
                          {translate('Content')}
                        </NavLink>
                      </NavItem>
                    )}
                    <NavItem>
                      <NavLink
                        className={activeNav === '3' ? 'actived' : ''}
                        onClick={() => setActiveNav('3')}
                      >
                        {translate('Performance')}
                      </NavLink>
                    </NavItem>
                  </Nav>
                  <TabContent activeTab={activeNav}>
                    {isLoading ? (
                      <>
                        <Shimmer height="22em" style={{ marginBottom: '3em' }} />
                        <Shimmer height="10em" style={{ marginBottom: '3em' }} />
                        <Shimmer height="15em" />
                      </>
                    ) : (
                      <TabPane tabId="1">
                        <TableResumeReview
                          data={content}
                          accountId={currentAccountId}
                          guidelineId={id}
                        />
                      </TabPane>
                    )}
                  </TabContent>
                  <TabContent activeTab={activeNav}>
                    <TabPane tabId="2">
                      <ContainerEditContent
                        content={content}
                        setIsPageLoading={setIsPageLoading}
                        noIA
                      />
                    </TabPane>
                  </TabContent>
                  <TabContent activeTab={activeNav}>
                    <TabPane tabId="3">
                      <PerformanceData
                        primaryKeyword={content?.primaryKeyword}
                        secondaryKeywords={content?.secondaryKeywords}
                      />
                    </TabPane>
                  </TabContent>
                </div>
                <div className="container-side-column">
                  <SendCardLayout>
                    <p className="subtitle">{translate('Action')}</p>
                    <GoBackButton
                      onClick={onBackReview}
                      style={{ backgroundColor: '#EDF2F7', color: '#4A5568' }}
                    >
                      {isSending ? (
                        <MoonLoader color="#fff" size={20} />
                      ) : (
                        translate('Go Back to Review')
                      )}
                    </GoBackButton>
                    <Button onClick={toggleModal} disabled={!isTasksCompleted}>
                      {isSending ? (
                        <MoonLoader color="#fff" size={20} />
                      ) : (
                        translate('Complete review')
                      )}
                    </Button>
                  </SendCardLayout>
                  <ApprovalChat setComplete={setIsTasksCompleted} />
                </div>
                <ModalCompleteReview
                  isOpen={isModalOpen}
                  toggle={toggleModal}
                  submitApproval={() => {
                    onSubmit();
                    toggleModal();
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </GuidelineLayout>
  );
};

export default InternalApproval;
